// =============================================================================
// Admin Role Detail Page — Read-only detail view for a single role
// =============================================================================

import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Spinner,
  Modal,
  ModalFooter,
} from '@tkpod/ui';
import { api } from '@/lib/api';
import type {
  AdminRoleDetail,
  PermissionGroup,
} from '@tkpod/shared';

// ---------------------------------------------------------------------------
// API response types
// ---------------------------------------------------------------------------

interface RoleDetailResponse {
  data: AdminRoleDetail;
}

interface PermissionsResponse {
  groups: PermissionGroup[];
}

interface RoleUsersResponse {
  data: RoleUserSummary[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface RoleUserSummary {
  id: string;
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function displayName(user: RoleUserSummary): string {
  const parts = [user.firstName, user.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : user.email;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminRoleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // -- Queries --------------------------------------------------------------
  const roleQuery = useQuery<RoleDetailResponse>({
    queryKey: ['admin-roles', id],
    queryFn: () => api.get<RoleDetailResponse>(`/api/v1/admin/roles/${id}`),
    enabled: !!id,
  });

  const permissionsQuery = useQuery<PermissionsResponse>({
    queryKey: ['admin-roles-permissions'],
    queryFn: () => api.get<PermissionsResponse>('/api/v1/admin/roles/permissions'),
    enabled: !!roleQuery.data,
  });

  const usersQuery = useQuery<RoleUsersResponse>({
    queryKey: ['admin-users', 'by-role', id],
    queryFn: () =>
      api.get<RoleUsersResponse>(`/api/v1/admin/users?roleId=${id}&limit=50`),
    enabled: !!id,
  });

  const role = roleQuery.data?.data;
  const users = usersQuery.data?.data ?? [];

  // -- Delete mutation ------------------------------------------------------
  const deleteMutation = useMutation({
    mutationFn: (roleId: string) =>
      api.delete<{ message: string }>(`/api/v1/admin/roles/${roleId}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      navigate('/roles');
    },
  });

  const openDeleteModal = useCallback(() => {
    deleteMutation.reset();
    setShowDeleteModal(true);
  }, [deleteMutation]);

  const closeDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    deleteMutation.reset();
  }, [deleteMutation]);

  // -- Group permissions by resource ----------------------------------------
  const groupedPermissions = useMemo(() => {
    if (!role || !permissionsQuery.data) return [];

    const groups = permissionsQuery.data.groups;
    return groups
      .map((group) => ({
        ...group,
        permissions: group.permissions.filter((p) =>
          role.permissions.includes(p.key),
        ),
      }))
      .filter((group) => group.permissions.length > 0);
  }, [role, permissionsQuery.data]);

  // Fallback: permissions not in any group (ungrouped)
  const ungroupedPermissions = useMemo(() => {
    if (!role || !permissionsQuery.data) return role?.permissions ?? [];

    const allKnownKeys = new Set(
      permissionsQuery.data.groups.flatMap((g) =>
        g.permissions.map((p) => p.key),
      ),
    );
    return role.permissions.filter((p: string) => !allKnownKeys.has(p));
  }, [role, permissionsQuery.data]);

  // -- Loading state --------------------------------------------------------
  if (roleQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" label="Loading role details..." />
      </div>
    );
  }

  // -- Error state ----------------------------------------------------------
  if (roleQuery.isError || !role) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <p className="text-sm text-red-400">
            {roleQuery.isError
              ? `Failed to load role: ${roleQuery.error?.message ?? 'Unknown error'}`
              : 'Role not found.'}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void roleQuery.refetch()}
            >
              Retry
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/roles')}
            >
              Back to Roles
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // -- Render ---------------------------------------------------------------
  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-text-tertiary">
        <Link
          to="/roles"
          className="hover:text-text-primary transition-colors"
        >
          Roles
        </Link>
        <span>/</span>
        <span className="text-text-primary">{role.name}</span>
      </nav>

      {/* Role info card */}
      <Card>
        <CardHeader
          title={role.name}
          description={role.description || 'No description provided.'}
          action={
            <div className="flex items-center gap-2">
              {role.isSystemRole && (
                <Badge variant="warning" size="sm">
                  System Role
                </Badge>
              )}
              {!role.isSystemRole && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/roles/${id}/edit`)}
                  >
                    Edit Role
                  </Button>
                  {role.userCount === 0 ? (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={openDeleteModal}
                    >
                      Delete
                    </Button>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-text-tertiary">
                      <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v.01M12 9v3m0 8a9 9 0 110-18 9 9 0 010 18z" />
                      </svg>
                      Cannot delete — {role.userCount} user{role.userCount !== 1 ? 's' : ''} assigned
                    </span>
                  )}
                </>
              )}
            </div>
          }
        />

        {/* Metadata grid */}
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <MetadataItem label="Slug">
            <code className="rounded bg-surface-tertiary px-1.5 py-0.5 text-xs text-text-secondary">
              {role.slug}
            </code>
          </MetadataItem>
          <MetadataItem label="Type">
            {role.isSystemRole ? (
              <Badge variant="warning" size="sm">System</Badge>
            ) : (
              <Badge variant="default" size="sm">Custom</Badge>
            )}
          </MetadataItem>
          <MetadataItem label="Users">
            <span className="text-sm text-text-primary font-medium">
              {role.userCount}
            </span>
          </MetadataItem>
          <MetadataItem label="Created">
            <span className="text-sm text-text-primary">
              {formatDate(role.createdAt)}
            </span>
          </MetadataItem>
        </div>
      </Card>

      {/* Permissions card */}
      <Card>
        <CardHeader
          title="Permissions"
          description={`This role has ${role.permissions.length} permission${role.permissions.length !== 1 ? 's' : ''}.`}
        />

        {role.permissions.length === 0 ? (
          <div className="mt-4 flex items-center justify-center py-8 text-sm text-text-tertiary">
            No permissions assigned to this role.
          </div>
        ) : permissionsQuery.isLoading ? (
          <div className="mt-4 flex items-center justify-center py-8">
            <Spinner size="sm" label="Loading permission groups..." />
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {groupedPermissions.map((group) => (
              <div key={group.resource}>
                <h4 className="text-sm font-semibold text-text-primary mb-2 flex items-center gap-2">
                  {group.label}
                  <Badge variant="primary" size="sm">
                    {group.permissions.length}
                  </Badge>
                </h4>
                <div className="flex flex-wrap gap-2">
                  {group.permissions.map((perm) => (
                    <Badge
                      key={perm.key}
                      variant="default"
                      size="sm"
                    >
                      {perm.label}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}

            {ungroupedPermissions.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-text-primary mb-2">
                  Other Permissions
                </h4>
                <div className="flex flex-wrap gap-2">
                  {ungroupedPermissions.map((key) => (
                    <Badge key={key} variant="default" size="sm">
                      {key}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Users card */}
      <Card>
        <CardHeader
          title="Assigned Users"
          description={`${users.length} user${users.length !== 1 ? 's' : ''} assigned to this role.`}
        />

        {usersQuery.isLoading ? (
          <div className="mt-4 flex items-center justify-center py-8">
            <Spinner size="sm" label="Loading users..." />
          </div>
        ) : usersQuery.isError ? (
          <div className="mt-4 rounded-md bg-red-500/10 px-4 py-3 text-sm text-red-400">
            Failed to load users assigned to this role.
          </div>
        ) : users.length === 0 ? (
          <div className="mt-4 flex items-center justify-center py-8 text-sm text-text-tertiary">
            No users are currently assigned to this role.
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto rounded-lg border border-border">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Last Login
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-surface">
                {users.map((user) => (
                  <tr
                    key={user.id}
                    onClick={() => navigate(`/admin-users/${user.id}`)}
                    className="cursor-pointer transition-colors hover:bg-surface-secondary"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-text-primary whitespace-nowrap">
                      {displayName(user)}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                      {user.email}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {user.isActive ? (
                        <Badge variant="success" size="sm" dot>
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="danger" size="sm" dot>
                          Inactive
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-tertiary whitespace-nowrap">
                      {user.lastLoginAt
                        ? formatDate(user.lastLoginAt)
                        : 'Never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Delete confirmation modal */}
      {!role.isSystemRole && (
        <Modal
          open={showDeleteModal}
          onClose={closeDeleteModal}
          title="Delete Role"
          description={`Are you sure you want to delete the role "${role.name}"? This action cannot be undone.`}
          size="sm"
        >
          {deleteMutation.isError && (
            <div className="mb-4 rounded-md bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
              {deleteMutation.error?.message ?? 'Failed to delete role.'}
            </div>
          )}

          <ModalFooter>
            <Button
              variant="ghost"
              onClick={closeDeleteModal}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deleteMutation.isPending}
              onClick={() => {
                if (id) deleteMutation.mutate(id);
              }}
            >
              Delete Role
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function MetadataItem({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-1">
        {label}
      </p>
      <div>{children}</div>
    </div>
  );
}
